import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCash } from '../../context/CashContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { fmtEur } from '../../lib/constants';
import { Banknote, CreditCard, Wallet, TrendingUp, FileText, Printer, Ban } from 'lucide-react';

const Tile = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-border p-3">
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
    <div className={`font-display font-bold mt-1 ${accent ? 'text-primary' : ''}`}>{value}</div>
  </div>
);

const SummaryRow = ({ label, value, strong }) => (
  <div className="flex items-center justify-between text-sm border-b border-border pb-2">
    <span className="text-muted-foreground">{label}</span>
    <span className={strong ? 'font-bold' : 'font-medium'}>{value}</span>
  </div>
);

export const CloseDayDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const { session, totals, closeDay, refresh } = useCash();
  const [cash, setCash] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const opening = Number(session?.opening_cash || 0);
  const tot = totals || { total_revenue: 0, total_cash: 0, total_card: 0 };
  const expected = tot.expected_cash != null ? tot.expected_cash : opening + (tot.total_cash || 0);
  const diff = cash === '' ? null : Math.round((Number(cash) - expected) * 100) / 100;

  const reset = () => { setCash(''); setResult(null); setBusy(false); };

  const submit = async () => {
    setBusy(true);
    try {
      const data = await closeDay(cash);
      setResult({ ...data.summary, z_label: data.z_label, session_id: data.session_id,
        storni: data.report?.storni || [], storni_count: data.report?.storni_count || 0, storni_total: data.report?.storni_total || 0 });
      toast.success(t('cash.dayClosedToast'));
      toast.info(t('cashExtra.zAutoPrinted'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('cash.closeError'));
      setBusy(false);
    }
  };

  const downloadZ = async () => {
    if (!result?.session_id) return;
    try {
      const res = await api.get(`/cash/report/z/${result.session_id}/pdf`, { responseType: 'blob' });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = u; a.download = `informe_${result.z_label || 'z'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(t('reportsCash.downloadError')); }
  };

  const printZ = async () => {
    if (!result?.session_id) return;
    try {
      const { data } = await api.post(`/cash/report/z/${result.session_id}/print`);
      toast[data.queued ? 'success' : 'info'](data.queued ? t('reportsCash.printQueued') : t('reportsCash.printNoPrinter'));
    } catch (e) { toast.error(t('reportsCash.downloadError')); }
  };

  const finish = async () => {
    reset();
    onOpenChange(false);
    await refresh(); // session becomes null -> gate shows the open-day screen again
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !result) reset(); if (!o && result) { finish(); return; } onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{result ? t('cash.dayClosed') : t('cash.closeDay')}</DialogTitle></DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Tile icon={Wallet} label={t('cash.openingFund')} value={fmtEur(opening)} />
              <Tile icon={TrendingUp} label={t('cash.totalRevenue')} value={fmtEur(tot.total_revenue)} accent />
              <Tile icon={Banknote} label={t('cash.cash')} value={fmtEur(tot.total_cash)} />
              <Tile icon={CreditCard} label={t('cash.card')} value={fmtEur(tot.total_card)} />
            </div>
            <div className="rounded-xl bg-muted/50 p-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('cash.expectedCashDesc')}</span>
              <span className="font-display font-bold" data-testid="close-expected">{fmtEur(expected)}</span>
            </div>
            <div>
              <Label>{t('cash.countedCash')}</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                <Input type="number" min="0" step="0.01" value={cash} onChange={(e) => setCash(e.target.value)} placeholder="0.00" className="h-12 pl-8 text-lg" data-testid="close-cash-input" />
              </div>
            </div>
            {diff != null && (
              <div className={`text-sm rounded-lg px-3 py-2 ${Math.abs(diff) < 0.01 ? 'bg-pos-free/10 text-pos-free' : 'bg-pos-bill/10 text-pos-bill'}`} data-testid="close-difference">
                {t('cash.difference')}: {diff > 0 ? '+' : ''}{fmtEur(diff)} {Math.abs(diff) < 0.01 ? t('cash.balanced') : (diff > 0 ? t('cash.surplus') : t('cash.shortage'))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
              <Button onClick={submit} disabled={busy || cash === ''} data-testid="close-day-submit">{busy ? t('cash.closing') : t('cash.closeDayBtn')}</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3" data-testid="close-result">
            <p className="text-sm text-muted-foreground">{t('cash.summaryDesc')}</p>
            {result.z_label && (
              <div className="rounded-xl bg-primary/10 text-primary px-4 py-3 flex items-center justify-between" data-testid="close-z-number">
                <span className="text-sm font-medium">{t('cashExtra.zNumber')}</span>
                <span className="font-mono font-bold text-lg">{result.z_label}</span>
              </div>
            )}
            <SummaryRow label={t('cash.openingFund')} value={fmtEur(result.opening_cash)} />
            <SummaryRow label={t('cash.totalRevenue')} value={fmtEur(result.total_revenue)} />
            <SummaryRow label={t('cash.cash')} value={fmtEur(result.total_cash)} />
            <SummaryRow label={t('cash.card')} value={fmtEur(result.total_card)} />
            <SummaryRow label={t('cash.expectedCash')} value={fmtEur(result.expected_cash)} />
            <SummaryRow label={t('cash.countedCashShort')} value={fmtEur(result.closing_cash)} />
            <SummaryRow label={t('cash.differenceLabel')} value={`${result.difference > 0 ? '+' : ''}${fmtEur(result.difference)}`} strong />
            {result.storni_count > 0 && (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 space-y-1.5" data-testid="close-storni">
                <div className="flex items-center justify-between text-sm font-semibold text-destructive">
                  <span className="flex items-center gap-1.5"><Ban className="h-3.5 w-3.5" /> {t('storno.reportTitle', { count: result.storni_count, defaultValue: `Storni (${result.storni_count})` })}</span>
                  <span>{fmtEur(result.storni_total)}</span>
                </div>
                <div className="max-h-32 overflow-auto space-y-1">
                  {result.storni.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="truncate mr-2">{s.operator} · {s.reason}</span>
                      <span className="whitespace-nowrap">{fmtEur(s.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={downloadZ} data-testid="close-z-download"><FileText className="h-4 w-4 mr-2" /> {t('cashExtra.downloadZ')}</Button>
              <Button variant="outline" className="flex-1 rounded-xl" onClick={printZ} data-testid="close-z-print"><Printer className="h-4 w-4 mr-2" /> {t('cashExtra.printZ')}</Button>
            </div>
            <DialogFooter><Button onClick={finish} data-testid="close-result-ok">{t('cash.finish')}</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
